import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { capitalizeEachFirstLetter, getErrorMessage } from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { BankCardDataType } from "src/lib/constants/table-type";
import moment from "moment";
import { EditIcon } from "../icons/edit-icon";
import routes from "src/config/routes";
import { useNavigate } from "react-router-dom";
import { useModalAction } from "../modal-views/context";

type UserPaymentMethodsPageProps = {
  user: User;
};

const UserPaymentMethodVirtualCards: FC<UserPaymentMethodsPageProps> = ({
  user,
}) => {
  const { canList, canView, canEdit } = usePermissions([
    permissionConstants.VIRTUAL_CARD_MANAGEMENT
  ]);
  const canListCards = canList || canView;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [dataSource, setDataSource] = useState([]);
  const { openModal } = useModalAction();
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const { mutate: getCards, isLoading: isIndividualCardsLoading } = useMutation(
    client?.userVirtualCards.getList,
    {
      onSuccess: (data) => {
        setTotal(data.totalcount || 0);

        setDataSource(
          data.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            card_number: item.masked_card_number,
            expiry_date: item.expiry_date,
            card_mode: item.card_mode,
            status: item.card_status,
            processing_status: item.processing_status,
            currency: item.asset?.code,
            gambling_control: item.gambling_control,
            created_at: item.createdAt,
            card_type_name: item.card_type?.name,
            phone: item.user_data?.phone,
            full_data: item,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({ ...searchParams, current_page: page });
  };

  useEffect(() => {
    if (!isMounted || !canListCards || !user?.id) {
      return;
    }

    getCards({ id: user.id, params: searchParams });
  }, [isMounted, user?.id, searchParams, canListCards]);

  const columns: ColumnsType<any> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Card Number
        </p>
      ),
      dataIndex: "card_number",
      key: "card_number",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Mode
        </p>
      ),
      dataIndex: "card_mode",
      key: "card_mode",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium capitalize">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Card Type
        </p>
      ),
      dataIndex: "card_type_name",
      key: "card_type_name",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {capitalizeEachFirstLetter(value) || "-"}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "currency",
      key: "currency",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium uppercase">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Processing Status
        </p>
      ),
      dataIndex: "processing_status",
      key: "processing_status",
      render: (value: string) => (
        <p
          className={`font-manrope text-[13px] sm:text-[14px] font-medium ${value === "active" ? "text-green-500" : "text-red-500"
            }`}
        >
          {capitalizeEachFirstLetter(value)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <p
          className={`font-manrope text-[13px] sm:text-[14px] font-medium ${value === "active" ? "text-green-500" : "text-red-500"
            }`}
        >
          {capitalizeEachFirstLetter(value)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Gambling Control
        </p>
      ),
      dataIndex: "gambling_control",
      key: "gambling_control",
      render: (value: boolean) => (
        <p
          className={`font-manrope text-[13px] sm:text-[14px] font-medium ${value ? "text-green-500" : "text-gray-400"
            }`}
        >
          {value ? "Enabled" : "Disabled"}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Expiry
        </p>
      ),
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (value: string) => {
        return (
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      key: "action",
      render: (_: any, record: any) => {
        if (!canEdit) {
          return <span className="text-secondary text-[13px]">—</span>;
        }

        return (
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (!canEdit) return;
                openModal("CARD_ACTION_MODAL", {
                  card: record.full_data,
                  callBackFn: () =>
                    getCards({ id: user.id, params: searchParams }),
                });
              }}
              title="Edit Card"
            >
              <EditIcon className="w-[18px] h-[18px]" />
            </button>
          </div>
        );
      },
    },
  ];

  if (!canListCards) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-[12px] shadow-sm">
        <p className="font-manrope text-[16px] font-semibold text-secondary">
          You do not have permission to view virtual card management details.
        </p>
      </div>
    );
  }

  return (
    <Table
      loading={isIndividualCardsLoading}
      showSorterTooltip={false}
      columns={columns}
      dataSource={dataSource}
      pagination={{
        total,
        current: searchParams.current_page,
        onChange: onPageChange,
        position: ["bottomCenter"],
        defaultPageSize: LIST_PAGE_SIZE,
        showSizeChanger: false,
      }}
    />
  );
};

export default UserPaymentMethodVirtualCards;
